import React, {
    useCallback,
    useContext,
    useEffect,
    useId,
    useState,
} from "react";
import { defineMessages, useIntl } from "react-intl";
import { useForm } from "react-hook-form";
import { customAlphabet, urlAlphabet } from "nanoid";
import { configStore } from "../../../../../../contexts/ConfigContext";
import Input from "../../../../../../design-system-components/Input/Input";
import { Button } from "@evidenceb/athena-common/design-system/Button";
import StudentList from "./StudentList/StudentList";
import { Student } from "../../../../../../interfaces/User";
import { sessionStore } from "../../../../../../contexts/SessionContext";
import { OverlayTriggerState } from "@react-stately/overlays";

import { Modal } from "@evidenceb/athena-common/design-system/Modal";
import RequiredInputLabelMarker from "../../../../../../components/RequiredInputLabelMarker/RequiredInputLabelMarker";
import { useCreateStudentsMutation } from "../../../api/mutations";

import "./AddStudentsModal.scss";
import { commonMessages } from "../../../../../../utils/messages";

export interface IFormValues {
    firstname: string;
    lastname: string;
}
interface StudentCreatorProps {
    classroom: {
        name: string;
        id: string;
    };
    onEnd: () => void;
    onCancel: () => void;
    state: OverlayTriggerState;
}

const AddStudentsModal = ({
    classroom,
    onEnd,
    onCancel,
    state,
}: StudentCreatorProps) => {
    const intl = useIntl();
    const nanoid = customAlphabet(urlAlphabet, 10);
    const {
        config: { declinaison },
    } = useContext(configStore);
    const {
        session: { fragments, userProvider },
    } = useContext(sessionStore);
    const {
        register,
        handleSubmit,
        reset,
        formState: { errors, isDirty },
    } = useForm<IFormValues>();

    const { mutate, isLoading } = useCreateStudentsMutation(classroom.id, {
        onSuccess: onEnd,
    });

    const [students, setStudents] = useState<Student[]>([]);

    const studentModalId = useId();
    const focusFirstNameInput = useCallback(() => {
        let input = document.getElementById(
            strongId(studentModalId, "firstName")
        );
        input?.focus();
    }, [studentModalId]);

    useEffect(() => {
        focusFirstNameInput();
    }, [focusFirstNameInput, studentModalId]);

    const handleCreateStudents = async () => {
        if (!students.length) return;
        const studentsData = students.map((student) => {
            return {
                ...student,
                classrooms: [classroom.id],
            };
        });
        mutate(studentsData);
    };

    const onStudentSubmit = async (data: IFormValues) => {
        const newStudent = {
            id: "",
            external_id: nanoid(),
            provider: userProvider,
            variation: declinaison,
            first_name: data.firstname,
            last_name: data.lastname,
            classrooms: [classroom.id],
            extra: {},
            createCode: true,
            config: {
                fragments_config: fragments,
            },
        } as Student;
        let newStudentsArray = students;
        newStudentsArray.push(newStudent);
        setStudents(newStudentsArray);
        reset();
        focusFirstNameInput();
    };

    const removeStudent = (id: string) => {
        setStudents(students.filter((student) => student.external_id !== id));
    };
    return (
        <Modal
            title={intl.formatMessage(messages.title, {
                classroomName: classroom.name,
            })}
            onClose={onCancel}
            onOk={handleCreateStudents}
            okColors={{
                background: "var(--element-teacher-default)",
                text: "var(--element-button-primary-text-hover)",
                hoverBackground: "var(--element-button-primary-hover)",
            }}
            cancelText={intl.formatMessage(commonMessages.cancel)}
            onOkDisabled={isLoading || students.length === 0}
            open={state.isOpen}
        >
            <div className="student-creator">
                <form onSubmit={handleSubmit(onStudentSubmit)}>
                    <div className="student-creator__firstname">
                        <label htmlFor={strongId(studentModalId, "firstName")}>
                            {intl.formatMessage(messages.firstNameLabel)}{" "}
                            <RequiredInputLabelMarker />
                        </label>
                        <Input
                            id={strongId(studentModalId, "firstName")}
                            error={
                                errors.firstname?.type === "required"
                                    ? intl.formatMessage(
                                          messages.firstNameRequired
                                      )
                                    : undefined
                            }
                            required
                            {...register("firstname", { required: true })}
                        />
                    </div>
                    <div className="student-creator__lastname">
                        <label htmlFor={strongId(studentModalId, "lastName")}>
                            {intl.formatMessage(messages.lastNameLabel)}{" "}
                            <RequiredInputLabelMarker />
                        </label>
                        <Input
                            id={strongId(studentModalId, "lastName")}
                            error={
                                errors.lastname?.type === "required"
                                    ? intl.formatMessage(
                                          messages.lastNameRequired
                                      )
                                    : undefined
                            }
                            required
                            {...register("lastname", { required: true })}
                        />
                    </div>
                    <Button
                        disabled={!isDirty}
                        label={intl.formatMessage(messages.add)}
                        icons={[
                            {
                                placement: "left",
                                path: "add",
                            },
                        ]}
                        type="submit"
                        size="full"
                        onClick={handleSubmit(onStudentSubmit)}
                    />
                </form>
                <StudentList
                    students={students}
                    onStudentDelete={removeStudent}
                />
            </div>
        </Modal>
    );
};

export default AddStudentsModal;

const strongId = (pageId: string, elementId: string) => {
    return `${pageId}-${elementId}`;
};

const messages = defineMessages({
    title: {
        id: "studentCreator-title",
        defaultMessage: "{classroomName} - add students",
    },
    firstNameLabel: {
        id: "studentCreator-firstName",
        defaultMessage: "First name",
    },
    lastNameLabel: {
        id: "studentCreator-lastName",
        defaultMessage: "Last name",
    },
    add: {
        id: "studentCreator-add",
        defaultMessage: "Add student",
    },
    addStudentCount: {
        id: "studentCreator-addStudentCount",
        defaultMessage:
            "Add {count, number} {count, plural, one {student} other {students}}",
    },
    firstNameRequired: {
        id: "studentCreator-error-firstNameRequired",
        defaultMessage: "First name is required",
    },
    lastNameRequired: {
        id: "studentCreator-error-lastNameRequired",
        defaultMessage: "Last name is required",
    },
});
